import { supabase } from "../lib/supabaseClient";

// ─── Flash Deals Types ───────────────────────────────────────────────────────

export interface FlashDealProduct {
  id: number;
  name: string;
  usd_price: number;
  display_price: number;
  sale_rate: number;
  sale_price: number;
  display_sale_price: number;
  image: string | null;
}

export interface FlashDeal {
  id: number;
  name: string;
  description: string | null;
  banner_path: string | null;
  start_date: string;
  end_date: string;
  seconds_remaining: number;
  products: FlashDealProduct[];
}

// ─── Flash Deals API ─────────────────────────────────────────────────────────

export async function getFlashDealsToday(
  currencyRate: number = 1,
): Promise<FlashDeal[]> {
  const { data, error } = await supabase.rpc("get_flash_deals_today", {
    p_currency_rate: currencyRate,
  });

  if (error) throw error;

  if (Array.isArray(data)) return data as FlashDeal[];
  if (data && typeof data === "object" && Array.isArray((data as any)[0])) {
    return (data as any)[0] as FlashDeal[];
  }
  return [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT PRODUCTS (get_client_products)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Payload & Response Types ────────────────────────────────────────────────

export interface GetProductsPayload {
  limit?: number;
  offset?: number;
  locale?: string;

  // Filters
  category_id?: number;
  brand_id?: number;
  sale_id?: number;
  is_featured?: boolean;
  is_on_sale?: boolean;
  in_stock?: boolean;
  min_price?: number;
  max_price?: number;
  search?: string;
  store_front?: boolean;

  // Sorting
  sort_by?: "created_at" | "price" | "name" | "popularity" | "usd_price";
  sort_order?: "asc" | "desc";
}

/** Shape of the JSON returned by `get_client_products`. */
export interface GetProductsResponse {
  status: "success" | "error";
  message: string;
  data: {
    products: RawClientProduct[];
    pagination: {
      limit: number;
      offset: number;
      total_count: number;
      has_more: boolean;
    };
  };
}

/** Single product object inside the `get_client_products` response. */
export interface RawClientProduct {
  id: number;
  name: string;
  description: string;
  usd_price: number;
  sale_price: number | null;
  sale_rate: number | null;
  final_price: number;
  is_on_sale: boolean;
  quantity: number;
  in_stock: boolean;
  weight: number | null;
  category_id: number;
  category_name: string;
  brand_id: number | null;
  brand_name: string | null;
  is_featured: boolean;
  popularity_score: number | null;
  average_rating: number;
  review_count: number;
  image_paths: string[];
  product_measurements: ProductMeasurement[];
  created_at: string;
  updated_at: string;
}

export interface ProductMeasurement {
  id: number;
  name: string;
  min_quantity: number;
  max_quantity: number;
  increase_by_range: number;
  conversion_rate: number;
  is_default: boolean;
  product_type_id: number;
  product_type_name: string;
}

// ─── API Call ────────────────────────────────────────────────────────────────

/**
 * Fetches products from the `get_client_products` RPC.
 *
 * This single function replaces:
 *  - `get_products_by_category_or_subcategory_v2h`
 *  - `search_products_v2h`
 *  - `get_on_sale_products_v2`
 *  - `get_recent_products_v2`
 *  - `fetch_featured_products_with_images`
 */
export async function getProducts(payload: GetProductsPayload = {}): Promise<{
  products: RawClientProduct[];
  totalCount: number;
  hasMore: boolean;
}> {
  const { data, error } = await supabase.rpc("get_client_products", {
    payload,
  });

  if (error) throw error;

  const response = data as unknown as GetProductsResponse;

  if (response.status === "error") {
    throw new Error(response.message || "Failed to fetch products");
  }

  return {
    products: response.data.products ?? [],
    totalCount: response.data.pagination.total_count ?? 0,
    hasMore: response.data.pagination.has_more ?? false,
  };
}
